import { Button } from '@crowdex/ui/components/button';
import { X } from 'lucide-react';
import {
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { PipAlignment } from '@/features/picture-in-picture/anchor-bridge';
import {
  PipCompleted,
  type PipItem,
  PipMedia,
  pipItemBox,
  pipItemSize,
  useRenderedPipItems,
} from '@/features/picture-in-picture/pip-media';
import { followPipStills } from '@/features/picture-in-picture/pip-stills';
import { electronInvoke } from '@/lib/electron-bridge';

/** What the Desktop shows in the floating stack (`PictureInPictureState` of the Desktop). */
type StackState = {
  items: PipItem[];
  alignment: PipAlignment;
  /** The longest side items fit in. */
  displaySize: number;
  /** The lead's anchored corner, in the window. */
  anchor: { x: number; y: number };
};

/** Under this distance a press is a click on the item; beyond, it drags the stack (Codex's 4). */
const DRAG_THRESHOLD = 4;
/** The corner handles resizing an item (Codex's 14 × 14). */
const HANDLE = 14;
const CORNERS: readonly PipAlignment[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

const invoke = (command: string, input: Record<string, unknown> = {}) =>
  void electronInvoke(command, input).catch(() => undefined);

/**
 * The floating window's page: the stack of what agents drive for the conversation shown, as
 * Codex's native one. The lead is in front, the others cascade behind it toward the window's
 * inside. Hovering any item shows the lead's close control (back into the summary panel); a press
 * drags the stack (the Desktop moves the window and settles it at a corner or in the panel); a
 * click on the lead brings what it shows to the front, on another item makes it lead; the corner
 * handles resize the items.
 */
export function PictureInPictureStack() {
  const [state, setState] = useState<StackState | null>(null);
  const rendered = useRenderedPipItems(state?.items ?? []);
  const [hovered, setHovered] = useState<string | null>(null);
  const press = useRef<{ id: string; x: number; y: number; dragging: boolean } | null>(null);
  /**
   * While pressed, dragged or resized the item stays hovered: the cursor may leave it for a moment
   * as the window follows.
   */
  const holding = useRef(false);

  useEffect(() => {
    // The window is transparent outside the stack.
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
    document.body.style.overflow = 'hidden';
    const stop = window.crowdexDesktop?.listen('picture-in-picture-state', (payload) =>
      setState(payload as StackState),
    );
    const unfollow = followPipStills(false);
    invoke('pip_ready');
    return () => {
      stop?.();
      unfollow();
    };
  }, []);

  const leave = () => {
    if (!holding.current) setHovered(null);
  };
  /** Released, the item under the pointer is the one hovered. */
  const release = (event: { clientX: number; clientY: number }) => {
    holding.current = false;
    const over = document
      .elementFromPoint(event.clientX, event.clientY)
      ?.closest<HTMLElement>('[data-pip-item]');
    setHovered(over?.dataset['pipItem'] ?? null);
  };

  if (state === null) return null;
  const { alignment, anchor, displaySize } = state;
  const items = rendered.filter((item) => !item.exiting);
  const leadId = items[0]?.id;

  const onPointerDown = (event: ReactPointerEvent, id: string) => {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    holding.current = true;
    press.current = { id, x: event.screenX, y: event.screenY, dragging: false };
  };
  const onPointerMove = (event: ReactPointerEvent) => {
    const current = press.current;
    if (current === null || current.dragging) return;
    if (Math.hypot(event.screenX - current.x, event.screenY - current.y) < DRAG_THRESHOLD) return;
    current.dragging = true;
    invoke('pip_drag_start', { id: current.id });
  };
  const onPointerUp = (event: ReactPointerEvent) => {
    const current = press.current;
    press.current = null;
    if (current === null) return;
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    if (current.dragging) invoke('pip_drag_end');
    else invoke('pip_interact', { id: current.id, focus: current.id === leadId });
    release(event);
  };
  const onPointerCancel = (event: ReactPointerEvent) => {
    if (press.current === null) return;
    if (press.current.dragging) invoke('pip_drag_end');
    press.current = null;
    release(event);
  };

  return (
    <div className="pointer-events-none fixed inset-0 select-none" data-picture-in-picture>
      {rendered.map((item) => {
        const index = item.exiting ? 0 : items.indexOf(item);
        const isLead = item.id === leadId;
        const style: CSSProperties = {
          ...pipItemBox(anchor, alignment, index, pipItemSize(displaySize, item.aspect)),
          zIndex: 100 - index,
        };
        return (
          <div
            key={item.id}
            data-pip-item={item.id}
            className={`pointer-events-auto absolute overflow-hidden rounded-[8px] bg-neutral-900 shadow-[0_6px_20px_rgb(0_0_0/0.16)] transition-[left,top,width,height] duration-300 ease-[cubic-bezier(0.34,1.2,0.64,1)] ${
              item.exiting ? 'pip-exit' : 'pip-enter'
            }`}
            onLostPointerCapture={onPointerCancel}
            onPointerCancel={onPointerCancel}
            onPointerDown={(event) => onPointerDown(event, item.id)}
            onPointerEnter={() => setHovered(item.id)}
            onPointerLeave={leave}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            style={style}
          >
            <PipMedia item={item} />
            {/* Codex's hover: white at 6 % over the content. */}
            {hovered === item.id && <div className="absolute inset-0 bg-white/[0.06]" />}
            {item.state === 'completed' && <PipCompleted icon={item.icon} />}
            {isLead && (
              <Button
                aria-label="Return Picture-in-Picture to Crowdex"
                className={`group absolute top-1.5 left-1.5 size-[22px] rounded-full bg-black/30 p-0 text-white backdrop-blur-md transition-opacity duration-[120ms] ease-in-out hover:bg-black/40 hover:text-white ${
                  hovered !== null ? 'opacity-100' : 'pointer-events-none opacity-0'
                }`}
                onClick={(event) => {
                  event.stopPropagation();
                  invoke('pip_return');
                }}
                onPointerDown={(event) => event.stopPropagation()}
                onPointerUp={(event) => event.stopPropagation()}
                size="icon-xs"
                title="Return Picture-in-Picture to Crowdex"
                variant="ghost"
              >
                <X className="size-3.5 opacity-70 group-hover:opacity-100" strokeWidth={3} />
              </Button>
            )}
            {!item.exiting &&
              CORNERS.map((corner) => (
                <div
                  aria-hidden
                  className={
                    corner === 'top-left' || corner === 'bottom-right'
                      ? 'cursor-nwse-resize'
                      : 'cursor-nesw-resize'
                  }
                  key={corner}
                  onPointerCancel={(event) => {
                    invoke('pip_resize_end');
                    release(event);
                  }}
                  onPointerDown={(event) => {
                    if (event.button !== 0) return;
                    event.stopPropagation();
                    event.currentTarget.setPointerCapture(event.pointerId);
                    holding.current = true;
                    invoke('pip_resize_start', { id: item.id, corner });
                  }}
                  onPointerUp={(event) => {
                    event.stopPropagation();
                    invoke('pip_resize_end');
                    release(event);
                  }}
                  style={{
                    position: 'absolute',
                    width: HANDLE,
                    height: HANDLE,
                    [corner.startsWith('top') ? 'top' : 'bottom']: 0,
                    [corner.endsWith('left') ? 'left' : 'right']: 0,
                  }}
                />
              ))}
          </div>
        );
      })}
    </div>
  );
}
